import styled from "styled-components";

/* ─── Design tokens ──────────────────────────────────────────────────────────
   Dark navy sidebar — contrasts sharply with the #f8fafc main content area.
   Brand blue (#035184) is the accent for active states and highlights.
─────────────────────────────────────────────────────────────────────────── */
const SB_BG        = "#0c1a2e";
const SB_HDR       = "#071020";
const SB_BORDER    = "rgba(255,255,255,0.06)";
const SB_TEXT      = "rgba(255,255,255,0.78)";
const SB_TEXT_MUTED = "rgba(255,255,255,0.35)";
const SB_ICON      = "rgba(255,255,255,0.55)";
const SB_HOVER_BG  = "rgba(255,255,255,0.055)";
const SB_ACTIVE_BG = "rgba(30, 123, 196, 0.18)";
const SB_ACTIVE_BORDER = "#1e7bc4";
const SB_ACTIVE_TEXT   = "#fff";
const SB_SECTION_BG    = "rgba(30, 123, 196, 0.08)";

const SidebarWrapper = styled.div`

  /* ── Outer shell ── */
  .sideBack {
    background: ${SB_BG};
    overflow: hidden;
    box-shadow: 2px 0 16px rgba(0,0,0,0.28);
    border-right: 1px solid rgba(255,255,255,0.04);
    margin-bottom: ${props =>
      props.themeSetting.footerAlignValue === "above" ? "50px" : "0px"};
    margin-top: 0;
  }

  /* ── Header (logo area) ── */
  .sidebar-header {
    cursor: pointer;
    position: relative;
    z-index: 4;
    padding: 8px 0.7rem;
    background: ${SB_HDR};
    border-bottom: 1px solid ${SB_BORDER};
  }

  .simple-text {
    text-transform: capitalize;
    white-space: nowrap;
    font-size: 1rem;
    color: #fff;
    font-weight: 400;
    line-height: 30px;
    text-decoration: none;
    overflow: hidden;
  }

  .logo-mini {
    opacity: 1;
    float: left;
    text-align: center;
    margin-left: 0;
    margin-right: 10px;
    padding: 8px 0 8px 6px;
    img {
      width: 46px;
      height: 40px;
      /* logo_white.png é branco puro — perfeito para o fundo navy */
      filter: drop-shadow(0 0 8px rgba(96, 196, 255, 0.35));
      transition: filter 0.2s;
    }
    &:hover img {
      filter: drop-shadow(0 0 12px rgba(96, 196, 255, 0.6));
    }
  }

  .logo-name {
    display: block;
    opacity: 1;
    transform: translateZ(0);
    padding: 13px 0 8px;
    img {
      height: 27px;
      /* Converte o texto escuro (#333) para branco no fundo navy */
      filter: brightness(0) invert(1);
      opacity: 0.88;
    }
  }

  /* ── Scrollbar ── */
  .sidebar-scrollbar-style {
    position: relative;
    display: block;
    width: 100%;
    cursor: pointer;
    border-radius: inherit;
    background: rgba(255,255,255,0.14) !important;
    opacity: 1;
  }

  /* ── Close icon ── */
  .close-drawer-icon {
    display: none;
    position: absolute;
    right: 15px;
    top: 18px;
    font-size: 20px;
    z-index: 5;
    cursor: pointer;
    color: ${SB_TEXT};
  }

  /* ── Section headings ── */
  .sidelist-header-name {
    text-transform: uppercase !important;
    font-weight: 700;
    display: flex;
    width: 100%;
    padding: 0.6rem 1.25rem 0.3rem;
    margin: 8px 0 0;
    color: ${SB_TEXT_MUTED} !important;
    font-size: 10px;
    letter-spacing: 0.1em;
    white-space: nowrap;
    position: relative;
    justify-content: space-between;
    align-items: center;

    &::before {
      content: "";
      display: block;
      width: 16px;
      height: 1px;
      background: ${SB_TEXT_MUTED};
      margin-right: 8px;
      flex-shrink: 0;
    }

    span { opacity: 0.55; }

    .new-update-tag {
      opacity: 1;
      position: relative;
      right: 0 !important;
      text-transform: capitalize !important;
      line-height: 30px;
    }
  }

  /* ── Nav wrapper layout ── */
  .sidebar-wrapper {
    display: flex;
    flex-direction: column;
  }

  /* ── Nav list ── */
  .sidebar-wrapper {

    .nav-link {
      color: ${SB_TEXT} !important;
      transition: color 0.15s, background 0.15s, padding-left 0.15s;

      &:hover {
        padding-left: 22px !important;
        background: ${SB_HOVER_BG} !important;
        color: #fff !important;
      }
    }

    /* Arrow chevron — dentro do <a.main-list> que tem position:relative inline */
    .arrow-sidebar {
      color: ${SB_TEXT_MUTED};
      font-size: 9px;
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
      transition: color 0.18s, transform 0.18s;
      z-index: 1;
      float: none;
      width: auto;
      margin: 0;
      line-height: 1;
      background: transparent !important;
    }

    .active-arrows {
      color: ${SB_ACTIVE_TEXT} !important;
    }

    /* ── Top-level item ── */
    .main-list {
      line-height: 30px;
      font-size: 14px;
      font-weight: 500;
      white-space: nowrap;
      position: relative;
      color: ${SB_TEXT};
      transform: translateZ(0);
      display: block;
      height: auto;
      opacity: 1;
      transition: all 0.15s linear;

      i {
        color: ${SB_ICON};
        font-size: 15px;
        width: 30px;
        height: 30px;
        line-height: 30px;
        text-align: center;
        border-radius: 8px;
        background: transparent;
        transition: color 0.18s, background 0.18s, box-shadow 0.18s;
        margin-right: 12px;
      }

      &:hover i {
        color: rgba(255,255,255,0.92);
        background: rgba(255,255,255,0.07);
      }

      p span { font-family: "muli-regular"; }
    }

    /* Ícone no estado ativo — glow azul */
    .active .main-list i,
    .active > i,
    li .active i {
      color: #60c4ff !important;
      background: rgba(30, 123, 196, 0.22) !important;
      box-shadow: 0 0 10px rgba(96, 196, 255, 0.25) !important;
    }

    /* ── Child item ── */
    .child-list {
      margin: 0;
      line-height: 28px;
      font-size: 13px;
      font-weight: 400;
      white-space: nowrap;
      position: relative;
      color: rgba(255,255,255,0.62);
      transform: translateZ(0);
      display: flex;
      align-items: center;
      height: auto;
      opacity: 1;
      transition: all 0.15s linear;
      padding-left: 16px;
      border-radius: 0 !important;

      &:hover {
        color: #fff !important;
        background: ${SB_HOVER_BG} !important;
        padding-left: 20px !important;
      }

      /* Ícone do item filho */
      .child-icon {
        font-size: 12px;
        width: 26px;
        height: 26px;
        line-height: 26px;
        text-align: center;
        border-radius: 6px;
        background: rgba(255,255,255,0.05);
        color: rgba(255,255,255,0.45);
        margin-right: 10px;
        flex-shrink: 0;
        transition: color 0.15s, background 0.15s;
        float: none;
      }

      &:hover .child-icon {
        color: rgba(255,255,255,0.8);
        background: rgba(255,255,255,0.09);
      }

      &.active .child-icon {
        color: #60c4ff !important;
        background: rgba(30, 123, 196, 0.2) !important;
      }

      p { margin: 0; }
    }

    /* ── Active state ── */
    .active {
      color: ${SB_ACTIVE_TEXT} !important;
      background: ${SB_ACTIVE_BG} !important;
      border-left: 3px solid ${SB_ACTIVE_BORDER} !important;
      font-weight: 600;
      border-radius: 0 6px 6px 0 !important;
      margin: 3px 10px 0 0 !important;

      i { color: #60c4ff !important; }

      .new-update-tag {
        letter-spacing: 1px;
        color: white !important;
        background: #ff4081 !important;
      }
    }

    /* ── Open section background ── */
    .sidebar-whole-list {
      background: ${SB_SECTION_BG};
    }

    /* ── style4 active sidebar type ── */
    .active-sidebar-type-class {
      border-left: 3px solid ${SB_ACTIVE_BORDER} !important;
      .child-list { margin: 5px 7px 0 !important; }
    }

    /* ── Second child icon ── */
    .second-child-list-icon { line-height: 25px !important; }
  }

  /* ── Parent layout ── */
  .parent {
    position: relative;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  /* ── Bottom button area ── */
  .sidebar-button {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 15px;
    flex-wrap: wrap;
    z-index: 10;
    position: relative;
  }

  .sidebar-button .btn {
    width: 80%;
    text-align: center;
    padding: 10px 15px;
    border-radius: 5px;
    font-size: 16px;
    cursor: pointer;
    background-color: #035184;
    color: white;
    border: none;
  }

  .sidebar-button .btn:hover { background-color: #023a63; }

  /* ── Collapse animations ── */
  .collapse {
    display: block !important;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.28s ease-in-out;
  }

  .collapse.show {
    max-height: 360px;
  }

  .card.background-transparent {
    background: transparent !important;
    box-shadow: none !important;
    border: none !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .card-body.pa-0 { padding: 0 !important; }

  /* ── WhatsApp support block ── */
  .sidebar-whatsapp-block {
    width: calc(100% - 20px);
    padding: 10px;
    margin: 8px 10px 12px;
    background: linear-gradient(
      135deg,
      rgba(0, 180, 100, 0.14) 0%,
      rgba(0, 150, 80, 0.08) 100%
    );
    border: 1px solid rgba(37, 211, 102, 0.25);
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.04);
  }

  .sidebar-whatsapp-block--mini {
    padding: 8px;
    margin: 8px 4px;
    width: calc(100% - 8px);
  }

  .sidebar-whatsapp-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1px;
  }

  .sidebar-whatsapp-title {
    font-size: 0.85rem;
    font-weight: 700;
    color: rgba(255,255,255,0.9);
    white-space: nowrap;
  }

  .sidebar-whatsapp-subtitle {
    font-size: 0.72rem;
    color: rgba(255,255,255,0.5);
    white-space: nowrap;
  }

  .sidebar-whatsapp-link {
    text-decoration: none;
    display: inline-flex;
  }

  .sidebar-whatsapp-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 7px 14px;
    background: rgba(37, 211, 102, 0.88) !important;
    border: 1px solid rgba(255,255,255,0.15) !important;
    border-radius: 8px;
    color: #fff !important;
    font-weight: 600;
    font-size: 0.82rem;
    cursor: pointer;
    transition: background 0.2s, transform 0.2s;
  }

  .sidebar-whatsapp-btn:hover {
    background: rgba(34, 190, 90, 1) !important;
    color: #fff !important;
  }

  .sidebar-whatsapp-btn .sidebar-whatsapp-icon,
  .sidebar-whatsapp-btn i { color: #fff !important; }

  .variant-hover {
    transition: transform 0.2s ease-in-out;
  }

  .variant-hover:hover {
    transform: scale(1.15);
  }
`;

export default SidebarWrapper;
